import { useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { addExpertRule, deleteExpertRule, updateExpertRule, useExpertRules, type ExpertRule } from './expertRulesStore';
import { smallBtnClass } from './controls';
import { said } from './drillParts';
import { useLanguage } from '../../contexts/LanguageContext';

// PHASE 4'S THIRD SUBPHASE, THE EXPERT RULES (Louis 2026-11-02): every rule, switched on or off,
// edited in place, added at the end, deleted. One list for every manual (see expertRulesStore); the ones
// on are in every sub-step call from the next run on.
export function ExpertRules({ locked }: { locked?: boolean }) {
  const { t } = useLanguage();
  const { rules, loaded } = useExpertRules();
  const [adding, setAdding] = useState(false);
  if (!loaded) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      {rules.length === 0 && <p className="text-center text-sm text-slate-400">{t('notice.rules.none')}</p>}
      {rules.map((r) => <RuleCard key={r.id} rule={r} locked={locked} />)}
      {adding
        ? <RuleForm onDone={() => setAdding(false)} onSave={(title, body) => addExpertRule(title, body)} />
        : !locked && (
          <div className="flex justify-center">
            <button type="button" className={smallBtnClass} onClick={() => setAdding(true)}>
              <Plus className="h-3.5 w-3.5" />{t('notice.rules.add')}
            </button>
          </div>
        )}
    </div>
  );
}

function RuleCard({ rule, locked }: { rule: ExpertRule; locked?: boolean }) {
  const { t } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [problem, setProblem] = useState('');
  const act = (p: Promise<unknown>) => { setProblem(''); p.catch((e) => setProblem(said(e))); };
  if (editing) {
    return <RuleForm rule={rule} onDone={() => setEditing(false)} onSave={(title, body) => updateExpertRule(rule.id, { title, body })} />;
  }
  return (
    <div className={`relative rounded-xl border p-3 ${rule.enabled ? 'border-slate-200' : 'border-slate-100 opacity-60'}`}>
      {/* CENTRED (Louis 2026-11-02), the switches in the corner and the text clear of them either side. */}
      <div className="px-28 text-center">
        <p className="text-sm font-semibold text-slate-800">{rule.title}</p>
        <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{rule.body}</p>
      </div>
      {!locked && (
          <div className="absolute right-3 top-3 flex items-center gap-1">
            <label className="flex cursor-pointer items-center gap-1 text-xs text-slate-500">
              <input
                type="checkbox"
                checked={rule.enabled}
                onChange={(e) => act(updateExpertRule(rule.id, { enabled: e.target.checked }))}
              />
              {t('notice.rules.on')}
            </label>
            <button type="button" aria-label={t('notice.rules.edit')} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" onClick={() => setEditing(true)}>
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label={t('notice.rules.delete')}
              className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
              onClick={() => act(deleteExpertRule(rule.id))}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
      )}
      {problem && <p className="mt-1 text-center text-xs text-rose-600">{problem}</p>}
    </div>
  );
}

function RuleForm({ rule, onSave, onDone }: {
  rule?: ExpertRule;
  onSave: (title: string, body: string) => Promise<void>;
  onDone: () => void;
}) {
  const { t } = useLanguage();
  const [title, setTitle] = useState(rule?.title ?? '');
  const [body, setBody] = useState(rule?.body ?? '');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const save = async () => {
    setBusy(true);
    setProblem('');
    try {
      await onSave(title.trim(), body.trim());
      onDone();
    } catch (e) {
      setProblem(said(e));
    }
    setBusy(false);
  };
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-blue-200 bg-blue-50/40 p-3">
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t('notice.rules.titlePh')}
        className="rounded-lg border border-slate-200 px-2 py-1 text-sm font-semibold text-slate-800"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={t('notice.rules.bodyPh')}
        rows={4}
        className="rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-700"
      />
      {problem && <p className="text-xs text-rose-600">{problem}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" className={smallBtnClass} onClick={onDone} disabled={busy}>{t('notice.rules.cancel')}</button>
        <button type="button" className={smallBtnClass} onClick={() => void save()} disabled={busy || !title.trim() || !body.trim()}>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{t('notice.rules.save')}
        </button>
      </div>
    </div>
  );
}
